import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Sparkles, Plus, MessageSquare, Trash2, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';
import ReactMarkdown from 'react-markdown';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CONVERSATIONS_KEY = 'advisor_ai_conversations';
const MAX_CONVERSATIONS = 20;

// Component to render markdown with LaTeX support
const MarkdownWithMath = ({ content }) => {
  if (!content) return null;
  
  // Split content by LaTeX blocks \[ ... \]
  const parts = content.split(/(\\\[[\s\S]*?\\\])/g);
  
  return (
    <div className="space-y-2">
      {parts.map((part, index) => {
        // Check if this part is a LaTeX block
        if (part.startsWith('\\[') && part.endsWith('\\]')) {
          const mathContent = part.slice(2, -2).trim();
          try {
            return (
              <div key={index} className="my-3 overflow-x-auto flex justify-center">
                <BlockMath math={mathContent} />
              </div>
            );
          } catch (e) {
            // Fallback to code block if KaTeX fails
            return <code key={index} className="block text-sm bg-gray-200 dark:bg-gray-600 p-2 rounded my-2">{mathContent}</code>;
          }
        }
        
        // Regular text - render with Markdown
        if (part.trim()) {
          return (
            <ReactMarkdown
              key={index}
              components={{
                p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                strong: ({ children }) => <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>,
                em: ({ children }) => <em className="italic">{children}</em>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                li: ({ children }) => <li className="ml-2">{children}</li>,
                code: ({ children }) => <code className="bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded text-sm">{children}</code>,
              }}
            >
              {part}
            </ReactMarkdown>
          );
        }
        return null;
      })}
    </div>
  );
};

// Copy button component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <button
      onClick={handleCopy}
      className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
      title={copied ? 'Copied!' : 'Copy response'}
    >
      {copied ? (
        <Check className="w-4 h-4 text-green-500" />
      ) : (
        <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white" />
      )}
    </button>
  );
};

export default function AdvisorAIChat({ user, onLogout }) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]); // All saved conversations
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const isMobile = useIsMobile();

  // User-specific localStorage key to prevent cross-user data leakage
  const getStorageKey = (userId) => {
    return userId ? `advisor_ai_conversations_${userId}` : null;
  };

  // Single effect to handle user change: clear state first, then load user-specific data
  useEffect(() => {
    // Always clear state first when user changes
    setConversations([]);
    setMessages([]);
    setActiveConversationId(null);
    
    // Then load the correct user's conversations
    if (!user?.id) return;
    
    const userKey = getStorageKey(user.id);
    if (!userKey) return;
    
    try {
      const saved = localStorage.getItem(userKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setConversations(parsed);
        }
      }
    } catch (e) {
      console.error('Error loading conversations:', e);
    }
  }, [user?.id]); // Re-run when user changes

  // Save conversations to localStorage (user-specific)
  const saveConversations = (convs) => {
    if (!user?.id) return;
    
    const userKey = getStorageKey(user.id);
    if (!userKey) return;
    
    try {
      localStorage.setItem(userKey, JSON.stringify(convs.slice(0, MAX_CONVERSATIONS)));
    } catch (e) {
      console.error('Error saving conversations:', e);
    }
  };

  // Update current conversation in the list
  const updateCurrentConversation = (newMessages) => {
    if (!activeConversationId || newMessages.length === 0) return;
    
    const updated = conversations.map(conv => 
      conv.id === activeConversationId 
        ? { ...conv, messages: newMessages, updatedAt: new Date().toISOString() }
        : conv
    );
    setConversations(updated);
    saveConversations(updated);
  };

  // Create a new conversation
  const handleNewChat = () => {
    // If current conversation has messages, save it first
    if (messages.length > 0 && activeConversationId) {
      updateCurrentConversation(messages);
    }
    
    // Create new conversation
    const newConv = {
      id: Date.now().toString(),
      title: 'New conversation',
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updated = [newConv, ...conversations].slice(0, MAX_CONVERSATIONS);
    setConversations(updated);
    saveConversations(updated);
    setActiveConversationId(newConv.id);
    setMessages([]);
    setInput('');
    toast.success('Started a new conversation');
  };

  // Load a conversation
  const loadConversation = (conv) => {
    // Save current conversation first
    if (messages.length > 0 && activeConversationId) {
      updateCurrentConversation(messages);
    }
    
    setActiveConversationId(conv.id);
    setMessages(conv.messages || []);
  };

  // Delete a conversation
  const deleteConversation = (e, convId) => {
    e.stopPropagation();
    const updated = conversations.filter(c => c.id !== convId);
    setConversations(updated);
    saveConversations(updated);
    
    // If we deleted the active conversation, load the first one or clear
    if (convId === activeConversationId) {
      if (updated.length > 0) {
        setActiveConversationId(updated[0].id);
        setMessages(updated[0].messages || []);
      } else {
        setActiveConversationId(null);
        setMessages([]);
      }
    }
    toast.success('Conversation deleted');
  };

  // Get conversation title from first message
  const getConversationTitle = (conv) => {
    if (conv.messages && conv.messages.length > 0) {
      const firstUserMsg = conv.messages.find(m => m.role === 'user');
      if (firstUserMsg) {
        return firstUserMsg.content.slice(0, 40) + (firstUserMsg.content.length > 40 ? '...' : '');
      }
    }
    return 'New conversation';
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    if (!user?.id) {
      toast.error('Please log in to use AI chat');
      return;
    }

    // Track the current conversation ID locally (since setState is async)
    let currentConvId = activeConversationId;
    let currentConversations = [...conversations];

    // If no active conversation, create one
    if (!currentConvId) {
      const newConv = {
        id: Date.now().toString(),
        title: 'New conversation',
        messages: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      currentConvId = newConv.id;
      currentConversations = [newConv, ...conversations];
      setConversations(currentConversations);
      setActiveConversationId(currentConvId);
    }

    const userMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/advisor/ai-chat`,
        { message: input },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );

      const aiMessage = { role: 'assistant', content: response.data.response };
      const finalMessages = [...newMessages, aiMessage];
      setMessages(finalMessages);
      
      // Update conversation in list using local variable (not stale state)
      const updated = currentConversations.map(conv => 
        conv.id === currentConvId 
          ? { ...conv, messages: finalMessages, updatedAt: new Date().toISOString() }
          : conv
      );
      setConversations(updated);
      saveConversations(updated);
      
    } catch (error) {
      console.error('AI Error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.' 
      };
      const finalMessages = [...newMessages, errorMessage];
      setMessages(finalMessages);
      
      // Update conversation even on error
      const updated = currentConversations.map(conv => 
        conv.id === currentConvId 
          ? { ...conv, messages: finalMessages, updatedAt: new Date().toISOString() }
          : conv
      );
      setConversations(updated);
      saveConversations(updated);
      
      toast.error('Failed to get AI response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const suggestedQuestions = [
    'Calculate SIP for ₹1 Cr goal in 15 years',
    'ELSS vs PPF - which is better?',
    'Tax on selling mutual funds after 2 years',
    'Explain SWP to my client simply'
  ];

  // Follow-up suggestions shown after AI responds
  const followUpSuggestions = [
    'Tell me more',
    'Explain simpler',
    'Compare alternatives',
    'Tax implications?'
  ];

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white flex flex-col relative overflow-hidden" data-testid="advisor-ai-chat-page">
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-purple-600/[0.06] to-pink-500/[0.06] blur-3xl" animate={{ x: [0, 20, 0], y: [0, -15, 0] }} transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }} style={{ top: '5%', right: '5%' }} />
        </div>
      )}
      <Navigation user={user} onLogout={onLogout} />
      
      <main className="flex-1 p-4 lg:p-8 relative z-10">
        <motion.div className="max-w-6xl mx-auto" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
          <div className="flex gap-6">
          {/* Conversation History Sidebar */}
          <motion.div className="w-72 flex-shrink-0 hidden lg:block" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <Card className="bg-white/80 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200">
                  History ({conversations.length})
                </h3>
                <Button
                  onClick={handleNewChat}
                  size="sm"
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white"
                  data-testid="new-chat-button"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </div>
              
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {/* New conversation placeholder - shown when in fresh chat state */}
                {!activeConversationId && (
                  <div
                    className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/40 border border-purple-300 dark:border-purple-500/50 flex items-start space-x-2"
                  >
                    <MessageSquare className="w-4 h-4 mt-0.5 text-purple-500 dark:text-purple-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 truncate">New conversation</p>
                      <p className="text-xs text-gray-500 mt-1">0 messages</p>
                    </div>
                  </div>
                )}
                
                {conversations.length === 0 && activeConversationId ? (
                  <p className="text-gray-500 text-sm text-center py-4">No conversations yet</p>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv)}
                      className={`p-3 rounded-lg cursor-pointer group flex items-start justify-between transition-all ${
                        activeConversationId && conv.id === activeConversationId
                          ? 'bg-purple-100 dark:bg-purple-900/40 border border-purple-300 dark:border-purple-500/50'
                          : 'bg-gray-100 dark:bg-gray-700/30 hover:bg-gray-200 dark:hover:bg-gray-700/50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start space-x-2 flex-1 min-w-0">
                        <MessageSquare className="w-4 h-4 mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-800 dark:text-gray-200 truncate">
                            {getConversationTitle(conv)}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {conv.messages?.length || 0} messages
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={(e) => deleteConversation(e, conv.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-500 hover:text-red-500 transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </motion.div>

          {/* Main Chat Area */}
          <div className="flex-1">
            {/* Mobile History Toggle + New Chat */}
            <div className="lg:hidden mb-4">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex-1 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 justify-between"
                >
                  <span className="flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    History ({conversations.length})
                  </span>
                  {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
                <Button
                  onClick={handleNewChat}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-4"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  New
                </Button>
              </div>
              
              {showHistory && (
                <Card className="bg-white/80 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 p-4 mt-2">
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {/* New conversation indicator for mobile */}
                    {!activeConversationId && (
                      <div className="p-2 rounded-lg text-sm bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300">
                        New conversation
                      </div>
                    )}
                    {conversations.map((conv) => (
                      <div
                        key={conv.id}
                        className={`p-2 rounded-lg text-sm flex items-center justify-between ${
                          activeConversationId && conv.id === activeConversationId
                            ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-300'
                            : 'bg-gray-100 dark:bg-gray-700/30 text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <span 
                          onClick={() => { loadConversation(conv); setShowHistory(false); }}
                          className="cursor-pointer flex-1 truncate"
                        >
                          {getConversationTitle(conv)}
                        </span>
                        <button
                          onClick={(e) => deleteConversation(e, conv.id)}
                          className="ml-2 p-1.5 text-gray-400 hover:text-red-500 flex-shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </div>

            <motion.div className="mb-4" initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}>
              <div className="flex items-center gap-3">
                <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-purple-500 to-pink-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.3 }} />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-testid="ai-chat-title">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-400 bg-clip-text text-transparent">Ask</span> FinAI
                  </h1>
                  <p className="text-gray-500 dark:text-gray-400 text-sm">Your financial co-pilot</p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
            <Card className="bg-white/80 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 p-6" data-testid="chat-container">
              <div className="h-[400px] overflow-y-auto mb-6 space-y-4" data-testid="messages-container">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <motion.div
                      className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 flex items-center justify-center mb-4"
                      animate={!isMobile ? { y: [0, -6, 0] } : {}}
                      transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                      <Sparkles className="w-7 h-7 text-purple-500 dark:text-purple-400" />
                    </motion.div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Start a conversation!</h3>
                    <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">Ask me anything about financial products or calculations</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-xl">
                      {suggestedQuestions.map((question, index) => (
                        <motion.button
                          key={index}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: 0.3 + index * 0.07 }}
                          whileHover={!isMobile ? { scale: 1.02, borderColor: 'rgb(168, 85, 247)' } : {}}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => setInput(question)}
                          className="p-3 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 hover:border-purple-500 transition-all text-left text-sm text-gray-700 dark:text-gray-300"
                        >
                          {question}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-4 rounded-2xl relative group ${
                          message.role === 'user'
                            ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Sparkles className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                              <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">AI Assistant</span>
                            </div>
                            <CopyButton text={message.content} />
                          </div>
                        )}
                        <div className="text-sm">
                          {message.role === 'assistant' ? <MarkdownWithMath content={message.content} /> : message.content}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="max-w-[85%] p-4 rounded-2xl bg-gray-100 dark:bg-gray-700">
                      <div className="flex items-center space-x-2">
                        <motion.div className="flex space-x-1" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                          {[0, 1, 2].map(i => (
                            <motion.div key={i} className="w-2 h-2 rounded-full bg-purple-500" animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, delay: i * 0.15, repeat: Infinity }} />
                          ))}
                        </motion.div>
                        <span className="text-gray-500 dark:text-gray-400 text-sm ml-1">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Follow-up suggestions after AI response */}
                {messages.length > 0 && !loading && messages[messages.length - 1]?.role === 'assistant' && (
                  <div className="flex flex-wrap gap-2 mt-4 justify-center" data-testid="followup-suggestions">
                    {followUpSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => setInput(suggestion)}
                        className="px-4 py-2 bg-gray-100 dark:bg-gray-700/60 border border-gray-300 dark:border-gray-600 rounded-full hover:bg-purple-100 dark:hover:bg-purple-900/40 hover:border-purple-500 transition-all text-sm text-gray-700 dark:text-gray-300"
                        data-testid={`followup-${index}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <Textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask about SIP, tax rules, product comparisons..."
                  className="resize-none bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 text-sm"
                  rows={2}
                  data-testid="message-input"
                />
                <Button
                  onClick={handleSend}
                  disabled={loading || !input.trim()}
                  className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white h-auto px-6 shadow-lg shadow-purple-600/20 disabled:shadow-none"
                  data-testid="send-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
            </Card>
            </motion.div>
          </div>
        </div>
        </motion.div>
      </main>
      
      <Footer />
    </div>
  );
}
